import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { AlineInput } from 'src/AlineInput';
import type { ExistingCollaboratorOption } from 'src/components';
import { Spacer, useHandleAddCollaborator, WorkflowCollaboratorsChooser } from 'src/components';
import { DragIcon } from 'src/components/icons/DragIcon';
import type { WorkflowAssignmentOption } from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import { Apollo } from 'src/gql/generated';
import { useAreWorkflowStepsEditable } from 'src/hooks/workflow/workflow';
import { getIsPlaceholderPermission } from 'src/types/apollo/workflow-permission';
import type { WorkflowStep } from 'src/workflow';
import {
  ActionDropdown,
  NewDeleteActionButton,
  useActionRowStyles,
  useNewWorkflowStep,
} from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

export const workflowCollaboratorChooserWidthPx = 280;
const useStyles = makeStyles()(theme => ({
  autocomplete: {
    width: workflowCollaboratorChooserWidthPx,
  },
  completedIcon: {
    color: theme.appColors.accent2,
  },
  nameInput: {
    width: 160,
  },
}));

interface NewActionRowProps {
  setNewActionRow: React.Dispatch<React.SetStateAction<boolean>>;
  step: WorkflowStep;
  workflowId: UUID;
}

export function NewActionRow({ setNewActionRow, step, workflowId }: NewActionRowProps) {
  const { classes } = useStyles();
  const { classes: actionRowClasses } = useActionRowStyles();
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { areStepsEditable } = useAreWorkflowStepsEditable(workflowId);
  const { handleAddCollaborator } = useHandleAddCollaborator(workflowId);
  const [collaborator, setCollaborator] = useState<ExistingCollaboratorOption | null>(null);
  const [recipientName, setRecipientName] = useState('');
  const { chosenAction, creatingAction, onSetChosenAction } = useNewWorkflowStep(
    [step],
    workflowId,
    Apollo.ActionPlacement.WithinStep,
    collaborator
  );

  // Scroll to the new action row when it is created
  useEffect(() => {
    if (ref) scrollIntoView(ref, { behavior: 'smooth', scrollMode: 'if-needed' });
  }, [ref]);

  // Returns the display name to auto-fill when the user picks an existing person.
  // Returns '' for roles and nameless guests (whose displayName is just their
  // email) so the name field is left blank instead of pre-filled with an email.
  function getAutoName(value: WorkflowAssignmentOption): string {
    switch (value.__typename) {
      case 'WorkflowPermission': {
        const { account } = value;
        if (account?.__typename !== 'User') return '';
        return account.displayName === account.email ? '' : account.displayName;
      }
      case 'User':
        return value.displayName === value.email ? '' : value.displayName;
      default:
        return '';
    }
  }

  return (
    <>
      <div ref={setRef} className={clsx(actionRowClasses.actionRow, classes)}>
        {areStepsEditable && <DragIcon height={16} visibility="hidden" width={16} />}
        <div className={classes.autocomplete}>
          <WorkflowCollaboratorsChooser
            emailOnly
            disabledReason={creatingAction ? 'Creating action...' : ''}
            value={collaborator}
            workflowId={workflowId}
            onChange={async (_, value) => {
              if (!value) return;

              // Always set (including '') so picking a role clears any previously typed name.
              setRecipientName(getAutoName(value));

              if (value.__typename === 'WorkflowPermission') {
                setCollaborator({ ...value, actions: [] });
                return;
              }

              const collaborator = await handleAddCollaborator(value);
              if (collaborator) setCollaborator(collaborator);
            }}
          />
        </div>
        {collaborator && !getIsPlaceholderPermission(collaborator) && (
          <AlineInput
            className={classes.nameInput}
            fullWidth={false}
            placeholder="Name"
            value={recipientName}
            onChange={e => setRecipientName(e.target.value)}
          />
        )}
        <Spacer />
        {collaborator && (
          <ActionDropdown
            assignee={{
              assignedRoleId: collaborator?.role?.id ?? null,
              assignedUserId:
                collaborator?.account?.__typename === 'User' ? collaborator.account.id : null,
              isLauncher: collaborator.isLauncher,
            }}
            chosenAction={chosenAction}
            disabled={!collaborator || creatingAction}
            workflowId={workflowId}
            onSetChosenAction={async action => {
              // || (not ??) so that an empty string is treated as no name.
              const nameToSave = getIsPlaceholderPermission(collaborator)
                ? null
                : recipientName || null;
              await onSetChosenAction(action, nameToSave);
              setNewActionRow(false);
            }}
          />
        )}
        <NewDeleteActionButton
          onClick={() => {
            setRecipientName('');
            setNewActionRow(false);
          }}
        />
      </div>
    </>
  );
}
